import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import moment from 'moment';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  shareReplay,
  skip,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

import { TicketService } from 'src/app/core/services/ticket';
import { PaginatedResult } from 'src/app/models/paginated-result.model';
import {
  CSVTicket,
  Ticket,
  TicketAggregate,
} from 'src/app/models/ticket/ticket.model';
import {
  isNonProactiveTicket,
  isTicketProactive,
} from 'src/app/models/ticket/ticket.util';
import * as fromStore from 'src/app/store/';
import { CreateTicketComponent } from '../create-ticket/create-ticket.component';

@Component({
  selector: 'app-tickets-shell',
  templateUrl: './tickets-shell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class TicketsShellComponent implements OnInit {
  ticketsValue: Ticket[] = [];

  readonly loadingAggregateStats = signal(false);

  readonly ticketAggregate = signal<TicketAggregate | null>(null);

  readonly ticketCsvData = signal<CSVTicket[]>([]);

  readonly isLoadingFilteredTickets = signal(false);

  page = 0;

  pageSize = 20;

  timeRange = 'all';

  searchTerm = '';

  selectedTicketId: string | null = null;

  selectedTicket: Ticket | null = null;

  private readonly store = inject(Store<fromStore.State>);

  private readonly destroyRef = inject(DestroyRef);

  private readonly dialog = inject(MatDialog);

  private readonly ticketService = inject(TicketService);

  readonly canCreateTicket = toSignal(
    combineLatest([
      this.store.select(fromStore.getContacts),
      this.store.select(fromStore.getContactsLoaded),
    ]).pipe(
      map(([contacts, loaded]) => !loaded || contacts?.length > 0),
      distinctUntilChanged(),
    ),
    { initialValue: true },
  );

  private readonly params$ = new BehaviorSubject<void>(undefined);

  readonly timeRange$ = new BehaviorSubject<string>('all');

  baseTickets$!: Observable<PaginatedResult<Ticket>>;

  filteredTickets$!: Observable<PaginatedResult<Ticket>>;

  ngOnInit(): void {
    firstValueFrom(this.store.select(fromStore.getContactsLoaded)).then(
      (loaded) => {
        if (!loaded) this.store.dispatch(new fromStore.LoadContacts());
      },
    );

    this.store
      .select(fromStore.getCurrentUser)
      .pipe(
        map((user) => user?.Account?.Name ?? user?.AccountId ?? null),
        distinctUntilChanged(),
        skip(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.selectedTicketId = null;
        this.selectedTicket = null;
        this.store.dispatch(new fromStore.SetSelectedTicket(null));
        this.loadTickets();
        this.loadAllTicketsForCsv();
      });

    this.baseTickets$ = this.params$.pipe(
      startWith(undefined),
      tap(() => {
        this.isLoadingFilteredTickets.set(true);
        this.loadingAggregateStats.set(true);
      }),
      switchMap(() =>
        this.ticketService.getTickets(
          this.page,
          this.pageSize,
          this.searchTerm,
          this.timeRange,
        ),
      ),
      shareReplay(1),
    );

    this.filteredTickets$ = combineLatest([
      this.baseTickets$,
      this.timeRange$,
    ]).pipe(
      map(([result, range]) => {
        if (!result?.data?.length) {
          this.loadTicketStats([]);
          return result;
        }
        const cloned = result.data.map((t) => ({ ...t }));
        const filtered = this.filterByTimeRange(cloned, range);
        this.loadTicketStats(filtered);
        return { ...result, data: filtered, total: filtered.length };
      }),
      tap((result) => {
        if (this.selectedTicketId && result?.data?.length) {
          const updated = result.data.find(
            (t) => t.Id === this.selectedTicketId,
          );
          if (updated) {
            const next = {
              ...updated,
              Comments__r: updated?.Comments__r
                ? {
                    ...updated.Comments__r,
                    records: Array.isArray(updated.Comments__r.records)
                      ? [...updated.Comments__r.records]
                      : [],
                  }
                : null,
            };

            if (
              this.selectedTicket !== next ||
              (this.selectedTicket?.Comments__r?.records?.length ?? -1) !==
                (next.Comments__r?.records?.length ?? -1)
            ) {
              this.selectedTicket = next;
            }
          }
        }

        if (!result?.data?.length) {
          this.selectedTicket = null;
          this.store.dispatch(new fromStore.SetSelectedTicket(null));
        }

        this.isLoadingFilteredTickets.set(false);
      }),
      shareReplay(1),
    );

    this.loadAllTicketsForCsv();
  }

  loadTickets(): void {
    this.params$.next();
  }

  loadTicketStats(tickets: Ticket[]): void {
    const result: TicketAggregate = {
      total: 0,
      open: 0,
      closed: 0,
      proactive: 0,
      nonProactive: 0,
      finance: 0,
      support: 0,
    };

    if (!tickets?.length) {
      this.ticketAggregate.set(result);
      this.loadingAggregateStats.set(false);
      return;
    }

    result.total = tickets.length;
    result.open = tickets.filter((t) => !t.IsClosed).length;
    result.closed = tickets.filter((t) => t.IsClosed).length;
    result.proactive = tickets.filter((t) => isTicketProactive(t)).length;
    result.nonProactive = tickets.filter((t) => isNonProactiveTicket(t)).length;

    this.ticketAggregate.set(result);
    this.loadingAggregateStats.set(false);
  }

  loadTicketStatsRedis(): void {
    this.loadingAggregateStats.set(true);
    this.ticketService
      .getAggregateOpenTotal(this.timeRange)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: TicketAggregate) => {
        this.ticketAggregate.set(result);
        this.loadingAggregateStats.set(false);
      });
  }

  onSelectTicket(t: Ticket): void {
    const id = t?.Id ?? null;
    this.selectedTicketId = id;
    this.selectedTicket = id ? t : null;
    this.store.dispatch(new fromStore.SetSelectedTicket(id));
  }

  openDialog(): void {
    this.dialog
      .open(CreateTicketComponent, {
        autoFocus: true,
        minHeight: '200px',
        maxHeight: 'calc(100vh - 20px)',
        width: '560px',
        maxWidth: 'calc(100vw - 20px)',
        data: {},
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.ticketSubmitted) {
          this.params$.next();
        }
      });
  }

  onTimeRangeChanged(timeRange: string): void {
    this.timeRange$.next(timeRange || 'all');
  }

  filterByTimeRange(tickets: Ticket[], range: string): Ticket[] {
    if (!range || range === 'all') return tickets;

    const now = moment.utc();

    switch (range) {
      case '6hours':
        return tickets.filter((t) =>
          moment
            .utc(t.CreatedDate)
            .isSameOrAfter(now.clone().subtract(6, 'hours')),
        );
      case '1day':
        return tickets.filter((t) =>
          moment
            .utc(t.CreatedDate)
            .isSameOrAfter(now.clone().subtract(1, 'day')),
        );
      case '1month':
        return tickets.filter((t) =>
          moment
            .utc(t.CreatedDate)
            .isSameOrAfter(now.clone().subtract(30, 'days')),
        );
      case '3months':
        return tickets.filter((t) =>
          moment
            .utc(t.CreatedDate)
            .isSameOrAfter(now.clone().subtract(90, 'days')),
        );
      default:
        return tickets;
    }
  }

  loadAllTicketsForCsv(): void {
    this.ticketService
      .getTickets(0, 10000, '', this.timeRange)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tickets) => {
        this.ticketCsvData.set(
          tickets?.data?.map((ticket) => ({
            'Ticket Number': ticket.CaseNumber,
            'Business Purpose': ticket.Business_Purpose__c?.length
              ? ticket.Business_Purpose__c
              : ticket.RecordType.Name,
            Title: ticket.Subject,
            Status: ticket.Status,
            Location: ticket.Service_Location_Address__c,
            Service: ticket.Service__r?.Name || null,
            'Maintenance Date':
              ticket.Maintenance_Date__c != null
                ? moment(ticket.Maintenance_Date__c).format('L')
                : null,
          })) ?? [],
        );
      });
  }
}
